import { useState } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bus, Loader2, Eye, EyeOff } from "lucide-react";

const SKYLINE =
  "https://images.unsplash.com/photo-1514565131-fce0801e5785?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxuZXclMjB5b3JrJTIwc2t5bGluZSUyMGNsZWFufGVufDB8fHx8MTc4MTgxNDk1Mnww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    const dest = user.must_change_password ? "/cambiar-password" : user.rol === "agencia" ? "/portal" : "/";
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      const from = location.state?.from?.pathname;
      const home = u.rol === "agencia" ? "/portal" : "/";
      navigate(u.must_change_password ? "/cambiar-password" : from || home, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No pudimos iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Form side */}
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
              <Bus className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-slate-900">Tours NY</div>
              <div className="text-xs text-slate-500 tracking-wide">· Reservas</div>
            </div>
          </div>

          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Iniciar sesión</h1>
          <p className="text-sm text-slate-500 mt-2 mb-8">
            Panel interno de operaciones. Ingresá con tu cuenta del equipo.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ingresar"}
            </Button>
            {/* PROMPT 96: recuperar la contraseña por correo. */}
            <div className="text-center">
              <Link to="/recuperar" className="text-sm text-slate-500 hover:text-slate-700 underline" data-testid="login-olvide-password">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>

          <p className="text-xs text-slate-400 mt-10">
            Tours NY · Reservas — Desarrollado por Olimpo para Alex Garcia.
          </p>
        </div>
      </div>

      {/* Image side */}
      <div className="hidden lg:block relative">
        <img src={SKYLINE} alt="Nueva York" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/55" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <h2 className="font-heading text-4xl font-bold text-white leading-tight max-w-md">
            Gestión centralizada de reservas y excursiones.
          </h2>
          <p className="text-slate-200 mt-4 max-w-md">
            Agenda en tiempo real, control de cupos y reglas de negocio, todo en un solo lugar para el
            equipo de operaciones.
          </p>
        </div>
      </div>
    </div>
  );
}
